import React from 'react';
import { Checkbox } from '../../../components/ui/checkbox';
import { Sparkles, RefreshCw, Loader2 } from 'lucide-react';

/**
 * FIT MATRIX with AI auto-assessment.
 *
 * Effective tick resolution per signal:
 *   - If the caller has MANUALLY set the signal (key present in fitSignals),
 *     that wins and LOCKS — later AI runs never overwrite it.
 *   - Otherwise the tick follows the AI: fitAi[signal].status === 'yes' => ticked.
 *   - AI 'unknown' / 'no' => unticked. 'no' is shown faintly with its reason.
 *
 * Clicking a signal writes a manual override (true/false) which persists via the
 * existing fit_signals PATCH and pins that signal against future AI runs.
 */
function FitMatrix({
  fitMatrix,
  fitSignals,
  fitAi,
  fitAiLoading,
  fitAiError,
  onSetFitSignal,
  onClearFitSignal,
  onRecheckFit,
}) {
  const signals = fitMatrix || [];
  const manual = fitSignals || {};
  const ai = fitAi || {};

  const isManual = (signal) => Object.prototype.hasOwnProperty.call(manual, signal);
  const aiStatus = (signal) => ai[signal]?.status || 'unknown';
  const aiEvidence = (signal) => ai[signal]?.evidence || '';

  // Effective tick: manual override if set, else AI 'yes'.
  const effectiveTick = (signal) =>
    isManual(signal) ? manual[signal] === true : aiStatus(signal) === 'yes';

  const yesCount = signals.filter((s) => effectiveTick(s.signal)).length;

  const onToggle = (signal, checked) => {
    // Toggling sets a manual override that locks the value in.
    onSetFitSignal(signal, checked === true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold tracking-wider text-white/40 uppercase">Fit Matrix</h3>
        <div className="flex items-center gap-2">
          {/* Transient assess failure: subtle, non-alarming. The last good
              verdicts are still shown below — this just signals the most recent
              re-check didn't land, and is itself a retry affordance. */}
          {fitAiError && !fitAiLoading && (
            <button
              type="button"
              onClick={onRecheckFit}
              title="Fit assessment couldn't run — tap to retry"
              className="text-[10px] text-white/40 hover:text-white/70 transition-colors lowercase"
            >
              couldn&apos;t assess — tap to retry
            </button>
          )}
          <span className="text-xs text-[#00d4aa] font-mono">{yesCount}/{signals.length}</span>
          <button
            type="button"
            onClick={onRecheckFit}
            disabled={fitAiLoading}
            title="Re-run AI fit assessment"
            className="text-white/40 hover:text-[#00d4aa] transition-colors disabled:opacity-50"
          >
            {fitAiLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
      </div>
      <div className="space-y-2.5">
        {signals.map((s) => {
          const manualSet = isManual(s.signal);
          const status = aiStatus(s.signal);
          const evidence = aiEvidence(s.signal);
          const ticked = effectiveTick(s.signal);
          // Show the AI badge only when the AI is driving the value (no manual
          // override) and it actually has an opinion (yes/no).
          const showAiBadge = !manualSet && status !== 'unknown';
          const showEvidence = showAiBadge && evidence;
          const isAiNo = !manualSet && status === 'no';

          return (
            <div key={s.signal} className="flex items-start gap-2 group">
              <Checkbox
                checked={ticked}
                onCheckedChange={(checked) => onToggle(s.signal, checked)}
                className="mt-0.5 cursor-pointer border-white/30 data-[state=checked]:bg-[#00d4aa] data-[state=checked]:border-[#00d4aa]"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span
                    className={`text-xs leading-snug cursor-pointer ${
                      isAiNo ? 'text-white/35' : 'text-white/70 group-hover:text-white'
                    }`}
                    onClick={() => onToggle(s.signal, !ticked)}
                  >
                    {s.signal}
                  </span>
                  {showAiBadge && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#00d4aa]/90 bg-[#00d4aa]/10 border border-[#00d4aa]/20 rounded px-1 py-px">
                      <Sparkles className="w-2.5 h-2.5" /> AI
                    </span>
                  )}
                  {manualSet && (
                    <button
                      type="button"
                      onClick={() => onClearFitSignal(s.signal)}
                      title="Clear manual override (let AI decide)"
                      className="text-[9px] uppercase tracking-wide text-white/30 hover:text-white/60"
                    >
                      reset
                    </button>
                  )}
                </div>
                {showEvidence && (
                  <p className="text-[10px] text-white/40 leading-tight mt-0.5">{evidence}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default FitMatrix;
